import { useState, useCallback } from 'react';
import { Team, TEAM_COLORS } from '../components/SortableEntity';
import { Player } from '../generated-api';

export interface RankedPlayer {
  id: string;
  player: Player;
  position: number;
  tiedWith: string[];
  teamId?: string;
}

export const useTeamManagement = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [rankedPlayers, setRankedPlayers] = useState<RankedPlayer[]>([]);

  const createTeam = useCallback((playerId: string) => {
    const newTeamId = `team-${Date.now()}`;

    setTeams(prevTeams => {
      const availableColor = TEAM_COLORS[prevTeams.length % TEAM_COLORS.length];
      const newTeam: Team = {
        id: newTeamId,
        name: `Team ${availableColor.name}`,
        color: availableColor.name.toLowerCase(),
        playerIds: [playerId],
      };
      return [...prevTeams, newTeam];
    });

    setRankedPlayers(prevRanked => 
      prevRanked.map(player => 
        player.id === playerId ? { ...player, teamId: newTeamId } : player
      )
    );
  }, []);

  const addPlayerToTeam = useCallback((playerId: string, teamId: string) => {
    setTeams(prev => prev.map(team => 
      team.id === teamId 
        ? { ...team, playerIds: [...team.playerIds, playerId] }
        : team
    ));
    setRankedPlayers(prev => prev.map(player => 
      player.id === playerId ? { ...player, teamId } : player
    ));
  }, []);

  const removePlayerFromTeam = useCallback((playerId: string) => {
    setTeams(prevTeams => 
      prevTeams
        .map(team => {
          if (!team.playerIds.includes(playerId)) {
            return team;
          }
          return {
            ...team,
            playerIds: team.playerIds.filter(id => id !== playerId),
          };
        })
        .filter(team => team.playerIds.length > 0) // Remove teams that became empty
    );

    setRankedPlayers(prevRanked =>
      prevRanked.map(p =>
        p.id === playerId ? { ...p, teamId: undefined } : p
      )
    );
  }, []);

  const getPlayerTeam = useCallback((playerId: string): Team | undefined => {
    return teams.find(team => team.playerIds.includes(playerId));
  }, [teams]);

  const getTeamColor = useCallback((teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return null;
    return TEAM_COLORS.find(color => color.name.toLowerCase() === team.color);
  }, [teams]);

  return {
    teams,
    setTeams,
    rankedPlayers,
    setRankedPlayers,
    createTeam,
    addPlayerToTeam,
    removePlayerFromTeam,
    getPlayerTeam,
    getTeamColor
  };
};